import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { take } from 'rxjs';

import { GraphQLService } from 'src/app/services/graph-ql.service';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { SnackbarType } from 'src/app/shared/components/snackbar/snackbar.component';
import { SettingsQueries } from 'src/app/shared/types/get-query/settings.enum';
import { Role } from '../../../models/types/role.type';
import { UserProfile } from '../../../models/types/user-profile.type';

export type AddUserProfileRoleModalData = {
	roleId: number;
	userProfileId: number;
	userProfileRoleId: number;
};

export type AddUserProfileRoleResult = {
	roleId: number;
	userProfileId: number;
	userProfileRoleId?: number;
};

@Component({
	standalone: false,
	selector: 'bt-add-user-profile-role-modal',
	templateUrl: './add-user-profile-role-modal.component.html',
	styleUrls: ['./add-user-profile-role-modal.component.scss']
})
export class AddUserProfileRoleModalComponent implements OnInit {
	readonly #dialogRef = inject(MatDialogRef<AddUserProfileRoleModalComponent>);
	readonly #graphQlService = inject(GraphQLService);
	readonly #utilitiesService = inject(UtilitiesService);
	readonly #destroyRef = inject(DestroyRef);
	readonly data = inject<AddUserProfileRoleModalData | undefined>(MAT_DIALOG_DATA, { optional: true });

	readonly isEdit = !!this.data?.userProfileRoleId;

	roles: Array<Role> = [];
	userProfiles: Array<UserProfile> = [];
	selectedRoleId: number | null = this.data?.roleId ?? null;
	selectedUserProfileId: number | null = this.data?.userProfileId ?? null;
	loading = false;
	#pendingRequests = 0;
	validation = {
		roleId: true,
		userProfileId: true
	};

	ngOnInit(): void {
		this.loadOptions();
	}

	close(): void {
		this.#dialogRef.close();
	}

	apply(): void {
		this.validation.roleId = this.selectedRoleId !== null;
		this.validation.userProfileId = this.selectedUserProfileId !== null;

		if (!this.validation.roleId || !this.validation.userProfileId) {
			return;
		}

		const result: AddUserProfileRoleResult = {
			roleId: this.selectedRoleId!,
			userProfileId: this.selectedUserProfileId!,
			...(this.isEdit ? { userProfileRoleId: this.data!.userProfileRoleId } : {})
		};

		this.#dialogRef.close(result);
	}

	onRoleChange(): void {
		this.validation.roleId = true;
	}

	onUserProfileChange(): void {
		this.validation.userProfileId = true;
	}

	private loadOptions(): void {
		this.loading = true;
		this.#pendingRequests = 2;

		this.#graphQlService
			.getData<any>(SettingsQueries.GET_ROLES)
			.pipe(take(1), takeUntilDestroyed(this.#destroyRef))
			.subscribe({
				next: (res: any) => {
					this.roles = (res.data?.roles?.nodes || []).map((node: any) => ({
						id: node.id,
						name: node.name
					}));
					this.#completeRequest();
				},
				error: (err) => {
					this.#completeRequest();
					this.#utilitiesService.showSnackbar('Error fetching roles', SnackbarType.Error);
					console.error(err);
				}
			});

		this.#graphQlService
			.getData<any>(SettingsQueries.GET_USER_PROFILES)
			.pipe(take(1), takeUntilDestroyed(this.#destroyRef))
			.subscribe({
				next: (res: any) => {
					this.userProfiles = (res.data?.userProfiles?.nodes || []).map((node: any) => ({
						id: node.id,
						name: node.name,
						companyId: node.companyId,
						platformId: node.platformId
					}));
					this.#completeRequest();
				},
				error: (err) => {
					this.#completeRequest();
					this.#utilitiesService.showSnackbar('Error fetching user profiles', SnackbarType.Error);
					console.error(err);
				}
			});
	}

	#completeRequest(): void {
		this.#pendingRequests = Math.max(0, this.#pendingRequests - 1);
		if (this.#pendingRequests === 0) {
			this.loading = false;
		}
	}
}
